import DriverPotentialPlanResponse, {
  DriverPotentialPlanResponseFullPlanEntryEndNode,
  DriverPotentialPlanResponseFullPlanEntryEndNodeMeta,
  DriverPotentialPlanResponseFullPlanEntrySolvedOrder,
  DriverPotentialPlanResponseFullPlanEntrySolvedOrderCargoCompany,
  DriverPotentialPlanResponseFullPlanEntrySolvedOrderRide,
  DriverPotentialPlanResponseFullPlanEntrySolvedOrderRideSeq,
  DriverPotentialPlanResponseFullPlanEntrySolvedOrderRideSeqMeta,
  DriverPotentialPlanResponseFullPlanEntrySolvedOrderTransportingOrder,
  DriverPotentialPlanResponseFullPlanEntrySolvedOrderTransportingOrderCargoOrder,
  DriverPotentialPlanResponseFullPlanEntryStartNode,
  DriverPotentialPlanResponseFullPlanEntryStartNodeMeta,
  DriverPotentialPlanResponsePlanEntry,
  DriverPotentialPlanResponsePlanEntryType,
} from "../../../api/raily/driver/potential-plan/driver-potential-plan.response";
import PlanEntryType from "../../../types/plan-entry-type";
import DriverPotentialPlan, {
  DriverPotentialFullPlanEntryEndNode,
  DriverPotentialFullPlanEntryEndNodeMeta,
  DriverPotentialFullPlanEntrySolvedOrder,
  DriverPotentialFullPlanEntrySolvedOrderCargoCompany,
  DriverPotentialFullPlanEntrySolvedOrderRide,
  DriverPotentialFullPlanEntrySolvedOrderRideSeq,
  DriverPotentialFullPlanEntrySolvedOrderRideSeqMeta,
  DriverPotentialFullPlanEntrySolvedOrderTransportingOrder,
  DriverPotentialFullPlanEntrySolvedOrderTransportingOrderCargoOrder,
  DriverPotentialFullPlanEntryStartNode,
  DriverPotentialFullPlanEntryStartNodeMeta,
  DriverPotentialPlanEntry,
} from "./driver-potential-plan";

const createEntryRestrictedPlanEntryStartNodeMeta = (
  meta: DriverPotentialPlanResponseFullPlanEntryStartNodeMeta,
): DriverPotentialFullPlanEntryStartNodeMeta => {
  return {
    estimatedTime: meta.estimatedTime,
    id: meta.id,
  };
};

const createEntryRestrictedPlanEntryStartNode = (
  startNode: DriverPotentialPlanResponseFullPlanEntryStartNode,
): DriverPotentialFullPlanEntryStartNode => {
  return {
    displayName: startNode.displayName,
    haltingTime: startNode.haltingTime,
    meta: createEntryRestrictedPlanEntryStartNodeMeta(startNode.meta),
    time: startNode.time,
  };
};

const createEntryRestrictedPlanEntryEndNodeMeta = (
  meta: DriverPotentialPlanResponseFullPlanEntryEndNodeMeta,
): DriverPotentialFullPlanEntryEndNodeMeta => {
  return {
    estimatedTime: meta.estimatedTime,
    id: meta.id,
  };
};

const createEntryRestrictedPlanEntryEndNode = (
  endNode: DriverPotentialPlanResponseFullPlanEntryEndNode,
): DriverPotentialFullPlanEntryEndNode => {
  return {
    displayName: endNode.displayName,
    haltingTime: endNode.haltingTime,
    meta: createEntryRestrictedPlanEntryEndNodeMeta(endNode.meta),
    time: endNode.time,
  };
};

const createEntryFullPlanEntryStartNodeMeta = (
  meta: DriverPotentialPlanResponseFullPlanEntryStartNodeMeta,
): DriverPotentialFullPlanEntryStartNodeMeta => {
  return {
    estimatedTime: meta.estimatedTime,
    id: meta.id,
  };
};

const createEntryFullPlanEntryStartNode = (
  startNode: DriverPotentialPlanResponseFullPlanEntryStartNode,
): DriverPotentialFullPlanEntryStartNode => {
  return {
    displayName: startNode.displayName,
    haltingTime: startNode.haltingTime,
    meta: createEntryFullPlanEntryStartNodeMeta(startNode.meta),
    time: startNode.time,
  };
};

const createEntryFullPlanEntryEndNodeMeta = (
  meta: DriverPotentialPlanResponseFullPlanEntryEndNodeMeta,
): DriverPotentialFullPlanEntryEndNodeMeta => {
  return {
    estimatedTime: meta.estimatedTime,
    id: meta.id,
  };
};

const createEntryFullPlanEntryEndNode = (
  endNode: DriverPotentialPlanResponseFullPlanEntryEndNode,
): DriverPotentialFullPlanEntryEndNode => {
  return {
    displayName: endNode.displayName,
    haltingTime: endNode.haltingTime,
    meta: createEntryFullPlanEntryEndNodeMeta(endNode.meta),
    time: endNode.time,
  };
};

const createEntryFullPlanEntrySolvedOrderRideSeqItemMeta = (
  meta: DriverPotentialPlanResponseFullPlanEntrySolvedOrderRideSeqMeta,
): DriverPotentialFullPlanEntrySolvedOrderRideSeqMeta => {
  return {
    estimatedTime: meta.estimatedTime,
    id: meta.id,
  };
};

const createEntryFullPlanEntrySolvedOrderRideSeqItem = (
  seqItem: DriverPotentialPlanResponseFullPlanEntrySolvedOrderRideSeq,
): DriverPotentialFullPlanEntrySolvedOrderRideSeq => {
  return {
    displayName: seqItem.displayName,
    haltingTime: seqItem.haltingTime,
    meta: createEntryFullPlanEntrySolvedOrderRideSeqItemMeta(seqItem.meta),
    time: seqItem.time,
  };
};

const createEntryFullPlanEntrySolvedOrderRideSeq = (
  seq: DriverPotentialPlanResponseFullPlanEntrySolvedOrderRideSeq[],
): DriverPotentialFullPlanEntrySolvedOrderRideSeq[] => {
  return seq.map(createEntryFullPlanEntrySolvedOrderRideSeqItem);
};

const createEntryFullPlanEntrySolvedOrderRide = (
  ride: DriverPotentialPlanResponseFullPlanEntrySolvedOrderRide,
): DriverPotentialFullPlanEntrySolvedOrderRide => {
  return {
    seq: createEntryFullPlanEntrySolvedOrderRideSeq(ride.seq),
  };
};

const createEntrySolvedOrderCargoCompany = (
  cargoCompany: DriverPotentialPlanResponseFullPlanEntrySolvedOrderCargoCompany,
): DriverPotentialFullPlanEntrySolvedOrderCargoCompany => {
  return {
    displayName: cargoCompany.displayName,
  };
};

const createEntrySolvedOrderTransportingOrderCargoOrder = (
  transportingOrder: DriverPotentialPlanResponseFullPlanEntrySolvedOrderTransportingOrderCargoOrder,
): DriverPotentialFullPlanEntrySolvedOrderTransportingOrderCargoOrder => {
  return {
    humanId: transportingOrder.humanId,
  };
};

const createEntrySolvedOrderTransportingOrder = (
  transportingOrder: DriverPotentialPlanResponseFullPlanEntrySolvedOrderTransportingOrder,
): DriverPotentialFullPlanEntrySolvedOrderTransportingOrder => {
  return {
    cargoOrder: createEntrySolvedOrderTransportingOrderCargoOrder(
      transportingOrder.cargoOrder,
    ),
  };
};

const createEntryFullPlanEntrySolvedOrder = (
  solvedOrder: DriverPotentialPlanResponseFullPlanEntrySolvedOrder,
): DriverPotentialFullPlanEntrySolvedOrder => {
  return {
    cargoCompany: createEntrySolvedOrderCargoCompany(solvedOrder.cargoCompany),
    ride: createEntryFullPlanEntrySolvedOrderRide(solvedOrder.ride),
    transportingOrder: createEntrySolvedOrderTransportingOrder(
      solvedOrder.transportingOrder,
    ),
  };
};

const createEntryFullPlanEntrySolvedOrders = (
  solvedOrders: DriverPotentialPlanResponseFullPlanEntrySolvedOrder[],
): DriverPotentialFullPlanEntrySolvedOrder[] => {
  return solvedOrders.map(createEntryFullPlanEntrySolvedOrder);
};

const createEntry = (
  entries: DriverPotentialPlanResponsePlanEntry,
): DriverPotentialPlanEntry => {
  switch (entries.viewType) {
    case DriverPotentialPlanResponsePlanEntryType.FULL:
      return {
        endNode: createEntryFullPlanEntryEndNode(entries.endNode),
        humanId: entries.humanId,
        id: entries.id,
        solvedOrders: createEntryFullPlanEntrySolvedOrders(
          entries.solvedOrders,
        ),
        startNode: createEntryFullPlanEntryStartNode(entries.startNode),
        viewType: PlanEntryType.FULL,
      };
    case DriverPotentialPlanResponsePlanEntryType.RESTRICTED:
      return {
        endNode: createEntryRestrictedPlanEntryEndNode(entries.endNode),
        humanId: entries.humanId,
        id: entries.id,
        startNode: createEntryRestrictedPlanEntryStartNode(entries.startNode),
        viewType: PlanEntryType.RESTRICTED,
      };
  }
};

const createEntries = (
  entries: DriverPotentialPlanResponsePlanEntry[],
): DriverPotentialPlanEntry[] => {
  return entries.map(createEntry);
};

const create = (response: DriverPotentialPlanResponse): DriverPotentialPlan => {
  return {
    entries: createEntries(response.data.entries),
  };
};

const driverPotentialPlanFactory = {
  create,
};

export default driverPotentialPlanFactory;
